import { m, type Message } from '@/i18n';
/**
 * The "how I got here" navigation trail, carried in the URL so it is honest,
 * shareable, and survives a refresh, and so browser Back pops it for free.
 *
 * The rule (per the design): a drilldown/item link *adds* to the path; a
 * nav-menu link to a nav-page is a plain URL with no trail, which *clears* it.
 * So the trail is a work-session breadcrumb that grows as you drill and resets
 * only on a deliberate top-level navigation.
 *
 *   from  - the view you started from (e.g. "/work" or "/groups/<id>"), the root crumb
 *   trail - the ids of items you clicked through before the current one
 *
 * A future second trail (the tree-position "minimap") is separate from this and
 * is derived from the decomposition, not the URL. This module is only the path.
 */

function one(value: string | string[] | undefined): string | undefined {
  if (Array.isArray(value)) return value[0];
  return value;
}

export interface TrailParams {
  from?: string;
  trailIds: string[];
}

/** Parse `from` / `trail` out of awaited Next searchParams. */
export function parseTrailParams(sp: Record<string, string | string[] | undefined>): TrailParams {
  const from = one(sp.from);
  const trailRaw = one(sp.trail);
  const trailIds = trailRaw ? trailRaw.split(',').filter(Boolean) : [];
  return { from: from && from.length > 0 ? from : undefined, trailIds };
}

/** Build the `?from=&trail=` suffix for an item link. Empty parts are omitted. */
export function buildTrailQuery(from: string | undefined, trailIds: string[]): string {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (trailIds.length > 0) params.set('trail', trailIds.join(','));
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * The href for an item link that extends the current path: the current item is
 * appended to the trail the child will carry. Used for every drilldown link.
 */
export function childHref(itemId: string, from: string | undefined, trailIds: string[]): string {
  return `/items/${itemId}${buildTrailQuery(from, trailIds)}`;
}

/** A root crumb's shape (`RootCrumb`, src/components/path-trail.tsx). */
export interface TrailRoot {
  href: string;
  /** A name a board or group holds as it is, or a descriptor. */
  label: string | Message;
  kind: 'home' | 'group' | 'search';
}

/**
 * The root crumb `from` names when no read is needed to label it: a search's
 * results, or the Work page for every other view and for a direct visit. A
 * board or a group is named by its row, so it yields null here and the
 * server's `rootCrumb` (src/app/items/[itemId]/view.ts) reads the name; the
 * tap's mount (src/components/tap-link.tsx) draws no root for it instead.
 */
export function unnamedRootCrumb(from: string | undefined): TrailRoot | null {
  if (from?.startsWith('/boards/') || from?.startsWith('/groups/')) return null;
  if (from?.startsWith('/search')) {
    return { href: from, label: m('shell.trail.root.search'), kind: 'search' };
  }
  return { href: '/work', label: m('shell.trail.root.work'), kind: 'home' };
}
